/**
 * A component to equalize the height of elements on the same row by group.
 */
class Equalizer {
  GROUP_SELECTOR = 'data-equalize-group';

  constructor(elements) {
    this.elements = elements;
    this.init();
    this.bindEvents();
  }

  init() {
    if (this.elements.length === 0) return;

    this.resetHeights();
    const groupElements = this.findGroupElements();
    const groups = this.groupElementsByAttributeAndRow(groupElements);
    this.equalizeHeights(groups);
  }

  findGroupElements() {
    const groupElements = [];
    this.elements.forEach(el => {
      const children = el.querySelectorAll(`[${this.GROUP_SELECTOR}]`);
      groupElements.push(...children);
    });
    return groupElements;
  }

  groupElementsByAttributeAndRow(elements) {
    const groups = {};

    elements.forEach(el => {
      const group = el.getAttribute(this.GROUP_SELECTOR) || 'default';
      const top = el.offsetTop;

      // Create a unique key combining group name and row position
      // Elements with offsetTop within 5px are considered on the same row
      const rowKey = this.findRowKey(groups, group, top);
      const groupRowKey = `${group}_${rowKey}`;

      if (!groups[groupRowKey]) {
        groups[groupRowKey] = [];
      }
      groups[groupRowKey].push(el);
    });

    return groups;
  }

  findRowKey(existingGroups, groupName, currentTop) {
    const tolerance = 5; // px tolerance for same row detection

    // Look for existing rows in the same group
    for (const key in existingGroups) {
      if (key.startsWith(groupName + '_')) {
        const elements = existingGroups[key];
        if (elements.length > 0) {
          const existingTop = elements[0].offsetTop;
          if (Math.abs(currentTop - existingTop) <= tolerance) {
            return key.split('_')[1];
          }
        }
      }
    }

    // No matching row found, create new row key
    return Math.round(currentTop);
  }

  equalizeHeights(groups) {
    Object.values(groups).forEach(group => {
      // Use getBoundingClientRect for more accurate height calculation
      const maxHeight = Math.max(...group.map(el => {
        const rect = el.getBoundingClientRect();
        const styles = window.getComputedStyle(el);
        const marginTop = parseFloat(styles.marginTop) || 0;
        const marginBottom = parseFloat(styles.marginBottom) || 0;
        return rect.height + marginTop + marginBottom;
      }));

      group.forEach(el => {
        // Set min-height instead of height to preserve content flow
        el.style.minHeight = `${maxHeight}px`;
      });
    });
  }

  resetHeights() {
    this.elements.forEach(el => {
      const children = el.querySelectorAll(`[${this.GROUP_SELECTOR}]`);
      children.forEach(child => {
        child.style.minHeight = '';
        child.style.height = '';
      });
    });
  }

  bindEvents() {
    this.destroy();

    this.resizeObserver = new ResizeObserver(() => {
      this.init();
    });

    // Observe the parent elements and the document body for layout changes
    this.elements.forEach(el => {
      this.resizeObserver.observe(el);
    });

    // Also observe the body to catch general layout changes
    this.resizeObserver.observe(document.body);
  }

  destroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const elements = document.querySelectorAll('[data-equalize]');

  if (elements.length > 0) {
    new Equalizer(elements);
  }
});
